/**
 * Store state for the `publicText` backport tool (`UI/Journal/Importer.tsx`, the Journal page's
 * "Importer" tab).
 *
 * This is reviewing state, not domain data: it holds the parsed drafts while the user works through
 * them, and nothing else in the app reads it. Two consequences:
 *  - the drafts are `@ignore`d by mobx-sync (they are hundreds of KB of markdown and are trivially
 *    re-created from the export file -- persisting them would bloat localStorage for no benefit), but
 *  - the VIEW settings (timezone offset, filters) ARE persisted, because losing them on every reload
 *    would be annoying while working through ~96 drafts.
 */
import {O} from "web-vcore";
import {ignore} from "mobx-sync";
import {DreamGroup, SkippedLog} from "../../JournalImport/DiscordExport.js";
import {ClosestSegmentMatch} from "../../JournalImport/ClosestMatch.js";

export const JournalImportDraftStatus_values = ["pending", "applied", "skipped"] as const;
export type JournalImportDraftStatus = typeof JournalImportDraftStatus_values[number];

/** How a draft's target segment is decided. `auto` = the matcher's suggestion (recomputed live, so it
 * survives entries loading late), `manual` = the user picked an entry/segment, `none` = do not apply. */
export const JournalImportTargetMode_values = ["auto", "manual", "none"] as const;
export type JournalImportTargetMode = typeof JournalImportTargetMode_values[number];

export type JournalImportDraft = {
	/** === `group.groupId` (the Discord id of the heading message); stable across reloads of the file */
	groupId: string;
	group: DreamGroup;
	/** the text that will be written to `publicText`; starts as `group.publicText`, editable */
	text: string;
	/** whether the preamble ("One lucid this morning:") is prepended to `text` when applying */
	includePreamble: boolean;
	targetMode: JournalImportTargetMode;
	manualEntryId: string|n;
	/** index into the chosen entry's `segments` array (wake markers included, matching the DB array) */
	manualSegmentIndex: number|n;
	status: JournalImportDraftStatus;
	/** true when the `skipped` status was set by the 100%-lines-matching rule rather than by the user */
	skippedAuto: boolean;
	/** set once the user has revived an auto-skipped draft (Unskip), so it is not auto-skipped again */
	autoSkipDismissed: boolean;
	appliedAt: number|n;
	/** free-form note the user can leave on a draft (eg. why it was skipped) */
	note: string;
};

export class JournalImporterState {
	/** Minutes EAST of UTC, used for BOTH sides of the date comparison (the Discord posts and the DB
	 * wake timestamps). Defaults to the browser's own offset, which is right when the export was made
	 * on the same machine/timezone the journal was written in. */
	@O accessor utcOffsetMinutes = -new Date().getTimezoneOffset();

	// view filters (persisted)
	@O accessor filterText = "";
	/** whether drafts whose match is NOT confident are listed. Defaults to true so the list starts
	 * unfiltered; untick it to keep only the drafts that are ready to apply as-is. */
	@O accessor showNonConfident = true;
	@O accessor showSkippedLogs = false;
	@O accessor showSkipped = false;
	@O accessor showApplied = false;
	/** whether a draft is expanded by DEFAULT; each draft can still be toggled individually (see
	 * `expandedOverrides`), so "collapse the ones I've already checked" works without un-expanding
	 * everything else too. */
	@O accessor startExpanded = false;

	/** `YYYY-MM-DD` (or empty for none). Posts from BEFORE this date are discarded while parsing the
	 * export, so they never become drafts or skipped-log entries. Persisted because it is a "everything
	 * before this is already committed" watermark, not a per-import setting. */
	@O accessor minDate = "";

	// imported data (NOT persisted -- see the file comment)
	@O @ignore accessor drafts: JournalImportDraft[] = [];
	/** per-draft expansion override (groupId -> expanded); absent = use `startExpanded` */
	@O @ignore accessor expandedOverrides: Record<string, boolean> = {};
	/** per-draft result of the "Search for match" button (groupId -> best line-overlap match found in
	 * the whole journal; `null` = the search ran but found no text to compare against). Not persisted:
	 * it is a lookup the user re-runs on demand, and a stale result would be worse than none. */
	@O @ignore accessor searchResults: Record<string, ClosestSegmentMatch|null> = {};
	@O @ignore accessor skippedLogs: SkippedLog[] = [];
	/** one-line summary of the last parse ("96 dreams from 3062 messages in 550 sessions") */
	@O @ignore accessor importSummary: string|n = null;
	@O @ignore accessor importError: string|n = null;
	@O @ignore accessor applyingGroupId: string|n = null;
	/** apply-log lines, newest last */
	@O @ignore accessor applyLog: string[] = [];
}

export function CreateJournalImportDraft(group: DreamGroup): JournalImportDraft {
	return {
		groupId: group.groupId,
		group,
		text: group.publicText,
		includePreamble: false,
		targetMode: "auto",
		manualEntryId: null,
		manualSegmentIndex: null,
		status: "pending",
		appliedAt: null,
		skippedAuto: false,
		autoSkipDismissed: false,
		note: "",
	};
}

/** The exact string that applying this draft would write to `publicText`. Shared by the apply path and
 * the review UI (which compares it against what the target segment already holds, to tell an
 * already-applied draft from a genuinely-confllicting one). */
export function GetDraftApplyText(draft: JournalImportDraft) {
	const preamble = draft.group.preamble.trim();
	if (draft.includePreamble && preamble.length > 0) return `${preamble}\n\n${draft.text}`;
	return draft.text;
}

/** A draft's expansion state: its own override if it has been toggled, else the `startExpanded` default. */
export function IsJournalImportDraftExpanded(state: JournalImporterState, groupId: string) {
	return state.expandedOverrides[groupId] ?? state.startExpanded;
}
